/**
 * devRolePreview.js — "render the app as another role", local dev only.
 *
 * Role-gated UI is easy to get wrong and nearly impossible to eyeball from an
 * admin account, because an admin passes every gate: you cannot tell a tab that
 * is correctly hidden from a TL apart from one that is broken for everyone. This
 * lets you flip the role the FRONTEND believes it has, so the chrome renders
 * exactly as that role sees it — which tabs exist, which sections are hidden,
 * which buttons are gone.
 *
 * ── WHAT IT CHANGES ────────────────────────────────────────────────────────
 * Two things, and they have to move together:
 *
 *   1. the `user` object AuthContext hands to the React tree — so the chrome
 *      renders as that role (which tabs exist, which sections are hidden);
 *   2. an X-Dev-Preview-Role header on every axios request — so the API answers
 *      as that role too.
 *
 * (1) alone was actively misleading: an admin previewing as 'tl' got a TL-shaped
 * page filled with admin data — the whole team archive, everyone's private
 * notes — which reads exactly like the TL clamp being broken. The header is what
 * makes the preview honest.
 *
 * The backend half (clarab/middleware/devRolePreview.js) is refused unless the
 * real account is admin/developer, so it can only ever REMOVE access, never
 * grant it. Your real account is never modified either way.
 *
 * WRITES ARE REAL. The downgraded role is more restricted, never less, but it is
 * still your account, and the local backend talks to the production database.
 * Archiving a ticket while previewing really archives it.
 *
 * ── WHY IT CANNOT LEAK INTO PRODUCTION ─────────────────────────────────────
 * Frontend: NODE_ENV must be 'development' (a CRA production build hard-codes
 * 'production', so this compiles out to a no-op) AND the page must be served
 * from localhost. Backend: DEV_ROLE_PREVIEW=1 must be set in clarab/.env and
 * NODE_ENV must not be 'production'. Any one gate failing disables the whole
 * thing, and the two halves are gated independently.
 *
 * Grants are cleared, not carried: previewing a role answers "what does a plain
 * <role> see", so an admin's personal qaCapabilities, featureAccess and
 * pagePermissions are dropped for the duration. Otherwise you would be
 * previewing "a TL who happens to hold tl-admin and goals-cross-agent", or a
 * kick-tl whose sidebar still shows QA Manager because YOUR account carries a
 * per-page override for it — nobody's real experience.
 */

import axios from 'axios';

const KEY = 'clara.devRolePreview';

/** Header the backend reads (middleware/devRolePreview.js). */
export const PREVIEW_HEADER = 'X-Dev-Preview-Role';

const ON_LOCALHOST = typeof window !== 'undefined'
  && /^(localhost|127\.0\.0\.1|\[::1\]|::1)$/.test(window.location.hostname);

/** Both gates. When false every function here is inert. */
export const DEV_PREVIEW_ENABLED = process.env.NODE_ENV === 'development' && ON_LOCALHOST;

/** User.role enum from clarab/models/User.js — keep in sync. */
export const PREVIEWABLE_ROLES = [
  'user',
  'tl',
  'kick-tl',
  'vip-host-tl',
  'qa',
  'qa-trainee',
  'qa-admin',
  'payments-qa',
  'poker-qa',
  'us-qa',
  'kyc-qa',
  'kpp-qa',
  'kick-qa',
  'host-qa',
  'host-qa-admin',
  'kick-mod-qa', 'kps-qa',
  'kyc-user',
  'kyc-admin',
  'payments-user',
  'payments-admin',
  'crypto-user',
  'crypto-admin',
  'audit',
  'ai-tester',
  'crm',
  'admin',
  'developer',
];

// Roles that may actually hold qaCapabilities grants (QA_GRANTABLE_ROLES in
// clarab/utils/qaCapabilities.js, plus the two bypass roles). Previewing
// anything else must show an empty capability set or the preview lies.
const CAP_HOLDING_ROLES = ['qa', 'qa-trainee', 'payments-qa', 'poker-qa', 'us-qa', 'kyc-qa', 'kpp-qa', 'kick-mod-qa', 'kps-qa', 'kick-qa', 'host-qa', 'qa-admin', 'host-qa-admin', 'admin'];

/** The role currently being previewed, or null. */
export function getDevPreviewRole() {
  if (!DEV_PREVIEW_ENABLED) return null;
  try {
    const v = localStorage.getItem(KEY);
    return v && PREVIEWABLE_ROLES.includes(v) ? v : null;
  } catch {
    return null;
  }
}

/**
 * Start or stop previewing. Reloads the page on purpose: plenty of components
 * read the role once at mount (route guards, effects with a role in their
 * dependency list), so a live context swap would leave a half-switched UI that
 * is worse than either state.
 */
export function setDevPreviewRole(role) {
  if (!DEV_PREVIEW_ENABLED) return;
  try {
    if (role && PREVIEWABLE_ROLES.includes(role)) localStorage.setItem(KEY, role);
    else localStorage.removeItem(KEY);
  } catch {
    return;
  }
  window.location.reload();
}

// Roles the backend lets act across QA workspaces (GLOBAL_TEAM_ROLES in
// clarab/config/qaTeams.js). The X-QA-Team header is 403'd for anything else.
const GLOBAL_TEAM_ROLES = ['admin', 'developer'];

/**
 * True when an active preview means the X-QA-Team header must NOT be sent.
 * Read by actingTeamHeaderValue() in constants/qaTeams.js — see the note there.
 */
export function devPreviewDropsTeamHeader() {
  const role = getDevPreviewRole();
  return !!role && !GLOBAL_TEAM_ROLES.includes(role);
}

/**
 * Put the preview role on every axios request (or take it off again).
 *
 * Called once at module scope below, so it is in place before the first request
 * fires — an interceptor installed later would let the initial profile fetch and
 * page load answer as the real role, which is precisely the mismatch this exists
 * to remove.
 */
export function installDevPreviewHeader() {
  if (!DEV_PREVIEW_ENABLED) return;
  const role = getDevPreviewRole();
  if (role) axios.defaults.headers.common[PREVIEW_HEADER] = role;
  else delete axios.defaults.headers.common[PREVIEW_HEADER];
}

installDevPreviewHeader();

/**
 * The user object the React tree should see. Returns the input unchanged when
 * no preview is active (or when previewing your own real role, which would be
 * a confusing no-op banner).
 */
export function applyDevPreview(user) {
  const role = getDevPreviewRole();
  if (!user || !role || role === user.role) return user;
  return {
    ...user,
    role,
    qaCapabilities: CAP_HOLDING_ROLES.includes(role) ? (user.qaCapabilities || []) : [],
    featureAccess: [],
    // Per-page overrides are set on YOUR user; the sidebar (components/sidebar/
    // navConfig.js hasPagePermission) would honour them over the previewed role.
    pagePermissions: undefined,
    // Read by the banner so it can name what you really are.
    __devPreview: { realRole: user.role, previewRole: role },
  };
}
